import { getAssistantSurface, type AddUserPrefill, type SettingsSurfaceHandle } from './assistant-actions'
import type { ExecuteClientToolDeps } from './client-tool-types'

const USERS_PATH = '/settings/users'
const ROLES = ['admin', 'publisher', 'viewer'] as const
type Role = (typeof ROLES)[number]

// The server's caps on a user's name and email.
const MAX_NAME = 200
const MAX_EMAIL = 320

/**
 * The users list reads before it registers, and a cold users Lambda can take several seconds to answer
 * it; past this the form is reported as not opened.
 */
const OPEN_WAIT_MS = 8_000
const POLL_MS = 100

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

function readText(value: unknown, max: number, label: string): string | undefined | { error: string } {
  if (value === undefined) return undefined
  if (typeof value !== 'string' || value.length > max) return { error: `Invalid ${label}.` }

  return value
}

/** Reads the model's input. There is deliberately no password field to read, so one sent is ignored. */
function readPrefill(input: unknown): AddUserPrefill | { error: string } {
  const raw = (input ?? {}) as { name?: unknown; email?: unknown; role?: unknown; guest?: unknown }
  const name = readText(raw.name, MAX_NAME, 'name')
  const email = readText(raw.email, MAX_EMAIL, 'email')

  if (typeof name === 'object') return name
  if (typeof email === 'object') return email
  if (raw.role !== undefined && !ROLES.includes(raw.role as Role)) {
    return { error: "Invalid role: expected 'admin', 'publisher' or 'viewer'." }
  }

  if (raw.guest !== undefined && typeof raw.guest !== 'boolean') return { error: 'Invalid guest flag.' }

  return {
    ...(name !== undefined ? { name } : {}),
    ...(email !== undefined ? { email } : {}),
    ...(raw.role !== undefined ? { role: raw.role as Role } : {}),
    // The form ignores the flag for an admin; do not offer one it would drop.
    ...(raw.guest === true && raw.role !== 'admin' ? { isGuest: true } : {}),
  }
}

/** The Users page's handle once its list has loaded and it can open the form. */
function usersHandle(): SettingsSurfaceHandle | null {
  const handle = getAssistantSurface()
  if (handle?.kind !== 'settings' || !handle.openAddUser) return null

  const ctx = handle.readContext().settings

  return ctx.view === 'users' && !ctx.loading ? handle : null
}

async function awaitUsersHandle(): Promise<SettingsSurfaceHandle | null> {
  const deadline = Date.now() + OPEN_WAIT_MS

  for (;;) {
    const handle = usersHandle()
    if (handle) return handle
    if (Date.now() >= deadline) return null
    await delay(POLL_MS)
  }
}

/**
 * `open_add_user`. `refusal` is the route manifest's answer for `/settings/users` for this caller (admin
 * only), resolved by `executeClientTool`, or null when allowed. Opens the form; the admin types the
 * password and creates the account.
 */
export async function executeOpenAddUser(
  input: unknown,
  deps: ExecuteClientToolDeps,
  refusal: string | null,
): Promise<unknown> {
  const prefill = readPrefill(input)
  if ('error' in prefill) return prefill

  if (refusal) return { refused: refusal }

  if (deps.currentRoute() !== USERS_PATH) {
    const discard = (input as { discardUnsaved?: unknown } | null)?.discardUnsaved === true

    if (!discard) {
      const unsaved = deps.hasUnsavedWork()
      if (unsaved) return { blocked_unsaved_work: unsaved }
    }

    deps.navigate(USERS_PATH)
  }

  const handle = await awaitUsersHandle()
  if (!handle) return { error: 'The Users page did not open in time.' }

  // The form defaults to viewer when no role is given, so that is the seat it would take.
  const role = prefill.role ?? 'viewer'
  const free = handle.freeSeats?.(role) ?? null

  if (free !== null && free <= 0) {
    return {
      opened: false,
      error: 'seats_full',
      message: `There is no free ${role} seat. Seats are bought in Billing.`,
    }
  }

  handle.openAddUser!(prefill)

  return { opened: true }
}
